import { compareNumericNullLast } from "@/components/table/compareNumeric";
import { type RegistryDatasetRef, type TrainingBudgetUnit, type TrainingPolicyView } from "@/api";
import { TableRow } from "@carbon6/c6-frontend-design";
import { formatBranchHash, shortSha } from "@/components/table/branchHash";
import { budgetUnitCellLabel } from "@/components/table/CheckpointCount";
import type { CheckpointTag } from "@/modules/training/public";
import { formatSuccessRate } from "@/modules/evaluation/public";
import { parseUtcMs } from "@/utils/datetime";
import type { PolicyFilterField, PolicyTextFilters } from "./policyFilterFields";
import { policyDatasetLabel } from "./trainingPolicyFormat";
import { formatSessionCreatedAt } from "./trainingSessionFormat";

const EMPTY_CELL = "—";

// 정책 카탈로그의 한 행. 열 키마다 행 필드 하나가 대응해야 하므로 표시 값을 평평하게 둔다.
// 등록된 정책은 읽기 전용이다. 표면이 활성화 핸들러를 전달하면 행 클릭이 그 핸들러를 호출한다.
// 예: Evaluation 페이지는 정책의 결과 창을 연다.
export class PolicyTableRow extends TableRow {
  readonly id: string;
  readonly name: string;
  // 정책을 만든 학습 세션. Policy Name 셀의 링크가 이 세션을 연다.
  readonly sourceSessionId: string;
  // 그 세션의 이름. 표에는 표시하지 않고, 링크가 여는 창의 제목 seed 로만 쓴다. 이름을 모르면
  // null 이다. 그때 호출부는 이름을 전달하지 않고, 상세 응답이 도착하면 제목을 고친다.
  readonly sessionName: string | null;
  readonly experimentLabel: string;
  // 한국어 학습 카탈로그 셀. 평가 변형은 epochValue 로 "#" 셀을 표시한다.
  readonly epochLabel: string;
  // 체크포인트 수의 단위와 그 "UNIT" 셀 문자열. 평가 변형에서만 쓴다.
  readonly budgetUnit: TrainingBudgetUnit;
  readonly unitLabel: string;
  // 코드 출처 셀. 같은 데이터셋에서 나온 정책들을 구분하는 축이다. 그래서 Results 표와 같은
  // 표기를 쓴다. 라벨은 정렬·필터·잘림 툴팁이 보는 문자열이다. 셀은 라벨을 다시 나누지 않고
  // 원본 두 조각을 받아 각 값을 복사한다.
  readonly branchHashLabel: string;
  readonly branch: string | null;
  readonly shortHash: string | null;
  // 검색 축이 full SHA 붙여넣기도 받도록 원문을 보관한다. 셀에는 표시하지 않는다.
  readonly fullSha: string | null;
  // 정렬용 원시값. 라벨은 빈 값을 대시로 채운다. 대시는 nullish 가 아니라 문자열이라
  // 오름차순에서 맨 앞으로 온다. 그래서 null 을 그대로 전달해 c6 의 nullish-last 규칙을 적용한다.
  readonly branchHash: string | null;
  readonly datasetLabel: string;
  // 학습 입력의 registry 원본. Dataset 셀을 링크로 표시할 때 쓴다.
  readonly datasetRef: RegistryDatasetRef | null;
  // eval-run 롤업 셀. 라벨은 셀과 잘림 툴팁이 보는 문자열이고, 원시값은 정렬용이다. 대시는
  // 문자열이라 오름차순 맨 앞으로 온다. 그래서 null 을 따로 보관해 뒤로 보낸다.
  readonly evalRunCount: number | null;
  readonly evalRunCountLabel: string;
  // 평균에 들어간 run 수. SR 셀 hover 가 몇 건의 평균인지 표시한다.
  readonly scoredRunCount: number | null;
  // Results 표의 SR[%] 와 같은 표기(0~1 → "92.5", % 기호 없음).
  readonly successRate: number | null;
  readonly successRateLabel: string;
  readonly ownerLabel: string;
  readonly createdLabel: string;
  readonly createdAtMs: number | null;
  // 정렬용 원시 체크포인트 수. epochLabel 을 쓸 수 없는 영어 표면도 이 값을 쓴다.
  readonly epochValue: number | null;
  // "#" 셀 hover 가 표시하는 세션 합계. 셀은 체크포인트 수를 표시한다.
  readonly sessionEpochValue: number | null;
  readonly sessionStepValue: number | null;
  // 이 정책이 된 체크포인트(단위+번호). Policy 이름 링크가 세션 창의 체크포인트 탭에서 그 행을
  // 가리킬 때 이 값을 보낸다. 번호를 모르면 null 이고, 링크는 탭만 연다.
  readonly checkpointTag: CheckpointTag | null;

  constructor(
    policy: TrainingPolicyView,
    private readonly onActivate: ((row: PolicyTableRow) => void) | null = null,
  ) {
    super();
    this.id = policy.id;
    this.name = policy.name;
    this.sourceSessionId = policy.source_session_id;
    this.sessionName = policy.session_display_name ?? null;
    this.experimentLabel = policy.experiment_name ?? EMPTY_CELL;
    this.budgetUnit = policy.budget_unit;
    this.unitLabel = budgetUnitCellLabel(this.budgetUnit);
    // 단위를 무시하면 step 예산 정책의 스텝 수를 "에폭 50000"으로 읽는다.
    this.epochLabel =
      policy.epoch !== null
        ? `${this.budgetUnit === "step" ? "스텝" : "에폭"} ${policy.epoch}`
        : EMPTY_CELL;
    this.sessionEpochValue = policy.source_session_total_epochs;
    this.sessionStepValue = policy.source_session_total_steps;
    // policy 는 full SHA 를 받는다. eval-run 은 BE 가 줄인 값을 받는다. 두 표의 자릿수를
    // 맞추려고 여기서 자른다.
    this.branch = policy.learning_core_ref ?? null;
    this.fullSha = policy.learning_core_sha ?? null;
    this.shortHash = shortSha(this.fullSha);
    this.branchHashLabel = formatBranchHash(this.branch, this.shortHash);
    this.branchHash = this.branch || this.shortHash ? this.branchHashLabel : null;
    this.datasetRef = policy.dataset_registry_ref ?? null;
    this.datasetLabel = policyDatasetLabel(policy) ?? EMPTY_CELL;
    const evalRuns = policy.eval_runs ?? null;
    this.evalRunCount = evalRuns?.count ?? null;
    this.evalRunCountLabel = this.evalRunCount !== null ? String(this.evalRunCount) : EMPTY_CELL;
    this.scoredRunCount = evalRuns?.scored_count ?? null;
    this.successRate = evalRuns?.success_rate_mean ?? null;
    this.successRateLabel = formatSuccessRate(this.successRate, null);
    this.ownerLabel = formatPolicyOwners(policy);
    // BE 는 naive UTC 로 직렬화한다. 로컬 시간으로 잘못 읽지 않도록 UTC 고정 파서를 쓴다.
    this.createdAtMs = parseUtcMs(policy.created_at);
    this.createdLabel = formatSessionCreatedAt(policy.created_at);
    this.epochValue = policy.epoch;
    this.checkpointTag =
      this.epochValue !== null ? { unit: this.budgetUnit, value: this.epochValue } : null;
  }

  getRowId(): string {
    return this.id;
  }

  // 오름차순으로 비교한다. 내림차순이면 DataTable 이 부호를 뒤집는다.
  // 에폭과 등록일은 원시값으로, 나머지 열은 셀 문자열로 비교한다.
  override compare(other: this, columnKey: string): number {
    switch (columnKey) {
      case "epochLabel":
        return compareNumericNullLast(this.epochValue, other.epochValue);
      case "createdLabel":
        return compareNumericNullLast(this.createdAtMs, other.createdAtMs);
      case "evalRunCountLabel":
        return compareNumericNullLast(this.evalRunCount, other.evalRunCount);
      case "successRateLabel":
        return compareNumericNullLast(this.successRate, other.successRate);
      // 라벨이 아니라 원시값으로 비교해 빈 값을 뒤로 보낸다.
      case "branchHashLabel":
        return super.compare(other, "branchHash");
      default:
        return super.compare(other, columnKey);
    }
  }

  // 검색 축이 비교하는 문자열. 셀이 표시하는 값과 같아야 사용자가 읽은 대로 거른다.
  private filterText(field: PolicyFilterField): string {
    switch (field) {
      case "name":
        return this.name;
      case "experiment":
        return this.experimentLabel;
      case "branch":
        // 셀은 8자로 줄여 표시하지만 붙여 넣는 값은 full SHA 인 경우가 많다. 라벨과 원문을
        // 둘 다 비교하므로 어느 쪽으로 검색해도 같은 행이 일치한다.
        return [this.branchHashLabel, this.fullSha ?? ""].join(" ");
      case "dataset":
        return this.datasetLabel;
      case "owner":
        return this.ownerLabel;
    }
  }

  // 축은 AND 로 좁힌다. needle 은 이미 소문자로 정규화되어 있다(chipsToPolicyTextFilters).
  matchesTextFilters(filters: PolicyTextFilters): boolean {
    return filters.every(({ key, needle }) => this.filterText(key).toLowerCase().includes(needle));
  }

  // 등록일 기간 필터. 등록 시각을 모르는 행은 창 안에 있다고 증명할 수 없으므로 제외한다.
  // 경계는 포함한다.
  isRegisteredSince(cutoffMs: number): boolean {
    return this.createdAtMs !== null && this.createdAtMs >= cutoffMs;
  }

  // 서버 필터가 보이는 행을 정한다. 그래서 반환된 행은 모두 보인다.
  canShow(): boolean {
    return true;
  }

  // 반환된 행은 모두 선택할 수 있다. 선택 상한은 source 가 소유한다.
  canSelect(): boolean {
    return true;
  }

  // 읽기 전용 호출부는 핸들러를 전달하지 않는다. 그래서 그 행은 반응하지 않는다(포인터 표시 없음).
  override get isRowClickable(): boolean {
    return this.onActivate !== null;
  }

  override onRowClick(): void {
    this.onActivate?.(this);
  }
}

// 소유자는 원본 Workspace 의 활성 멤버다. 소유자가 없는 정책은 em dash 를 표시한다.
export function formatPolicyOwners(policy: TrainingPolicyView): string {
  return policy.owner_member?.display_name ?? EMPTY_CELL;
}
